import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, inject, Signal } from '@angular/core';
import { Router } from '@angular/router';
import { createCustomStateSetOnUrlHandler } from '@appState/helpers/state-reset-handler';
import { addEntity, deleteEntity, getAll, StateDictionary, updateEntity } from '@appState/helpers/store-base';
import { FieldExpert, FieldExpertCreateRequest, FieldExpertUpdateRequest } from '@models/index';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { FileStorageService, NotificationsService } from 'cui-components';
import { catchError, EMPTY, pipe, switchMap, tap } from 'rxjs';
import { FieldExpertsService } from './field-experts.service';

interface FieldExpertsState {
  fieldExperts: FieldExpert[];
  selectedExpertId: string | null;
  isLoading: boolean;
  isExpertCreateLoading: boolean;
}

const initialState: FieldExpertsState = {
  fieldExperts: [],
  selectedExpertId: null,
  isLoading: false,
  isExpertCreateLoading: false,
};

const fieldExpertsDictionary: StateDictionary<FieldExpert> = {
  entityList: 'fieldExperts',
  listLoading: 'isLoading',
  createLoading: 'isExpertCreateLoading',
  selectedEntityLoading: 'isLoading',
  selectedEntity: '',
  entityId: 'id',
  sortFn: (a: FieldExpert, b: FieldExpert) => a.lastName.localeCompare(b.lastName),
};

export const FieldExpertsStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withDevtools('field-experts-store'),
  withComputed(state => ({
    selectedExpert: computed(() => {
      const experts = state.fieldExperts();
      const selectedId = state.selectedExpertId();
      return selectedId ? experts.find((expert: FieldExpert) => expert.id === selectedId) : null;
    }),
  })),
  withMethods(
    (
      store,
      fieldExpertsService = inject(FieldExpertsService),
      router = inject(Router),
      fileStorageService = inject(FileStorageService),
      notificationsService = inject(NotificationsService),
    ) => {
      createCustomStateSetOnUrlHandler<FieldExpertsState>('/marketing/field-experts', {
        selectedExpertId: null,
      })(store);

      const repositoryMethods = {
        getAll: getAll<FieldExpert>({
          store,
          service: fieldExpertsService,
          dictionary: fieldExpertsDictionary,
        }),

        update: updateEntity<FieldExpert, FieldExpertUpdateRequest>({
          store,
          service: fieldExpertsService,
          dictionary: fieldExpertsDictionary,
          onSuccessCallback: (updatedExpert: FieldExpert) => {
            patchState(store, { selectedExpertId: updatedExpert.id });
          },
        }),

        add: addEntity<FieldExpert, FieldExpertCreateRequest>({
          store,
          service: fieldExpertsService,
          dictionary: fieldExpertsDictionary,
          onSuccessCallback: (newExpert: FieldExpert) => {
            patchState(store, { selectedExpertId: newExpert.id });
            router.navigate(['/marketing', 'field-experts', newExpert.id, 'details']);
          },
        }),

        delete: deleteEntity<FieldExpert>({
          store,
          service: fieldExpertsService,
          dictionary: fieldExpertsDictionary,
          onSuccessCallback: () => {
            patchState(store, { selectedExpertId: null });
            router.navigate(['/marketing', 'field-experts']);
          },
        }),

        activateFileAndUpdate: rxMethod<{ file: { id: string }; entity: { id: string; dto: FieldExpertUpdateRequest } }>(
          pipe(
            tap(() => patchState(store, { isLoading: true })),
            switchMap(({ file, entity }) =>
              fileStorageService.activateFile(file.id).pipe(
                tap(() => {
                  patchState(store, {
                    isLoading: false,
                  });

                  repositoryMethods.update({ id: entity.id, dto: { ...entity.dto } });
                }),
                catchError(() => {
                  notificationsService.showNotification('Failed to upload file, please try again.');
                  patchState(store, { isLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),

        deleteFile: rxMethod<{ id: string }>(
          pipe(
            tap(() => patchState(store, { isLoading: true })),
            switchMap(({ id }) =>
              fileStorageService.deleteFile(id).pipe(
                tap(() => {
                  patchState(store, {
                    isLoading: false,
                  });
                }),
                catchError(() => {
                  patchState(store, { isLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),
      };

      const navigationActions = {
        selectFieldExpert: (id: string): void => {
          if (!store.fieldExperts().length && !store.isLoading()) repositoryMethods.getAll();

          patchState(store, { selectedExpertId: id });
        },

        goToActions: async (id: string): Promise<boolean> => {
          const result = await router.navigate(['/marketing', 'field-experts', id]);
          if (!result) return false;
          patchState(store, { selectedExpertId: id });

          return result;
        },
      };

      return {
        ...repositoryMethods,
        ...navigationActions,
      };
    },
  ),
);

export interface FieldExpertsStoreType {
  readonly fieldExperts: Signal<FieldExpert[]>;
  readonly isLoading: Signal<boolean>;
  readonly isExpertCreateLoading: Signal<boolean>;
  readonly selectedExpert: Signal<FieldExpert | null | undefined>;
  readonly selectedExpertId: Signal<string | null>;

  readonly getAll: () => void;
  readonly add: (dto: FieldExpertCreateRequest) => void;
  readonly update: (input: { id: string; dto: FieldExpertUpdateRequest }) => void;
  readonly activateFileAndUpdate: (input: { file: { id: string }; entity: { id: string; dto: FieldExpertUpdateRequest } }) => void;
  readonly delete: (id: string) => void;
  readonly selectFieldExpert: (id: string) => void;
  readonly deleteFile: (input: { id: string }) => void;
  readonly goToActions: (id: string) => Promise<boolean>;
}
